import { useCallback } from 'react';
import { useWorkflowStore } from '@/state/workflow.state';
import { DEFAULT_NODE_WIDTH } from '@/consts/node-ui.consts';
import { PACK_UNPACK_EVENT_NAMES } from '@/consts/analytics.consts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useFlowView } from '../Recipe/FlowContext';
import { createNewNode } from '../Recipe/useGraph';
import { getAbsolutePositionFromRelative, getIteratorInfo } from './utils/nodeUtils';
import { PACK_UNPACK_GAP } from './packUnpack.consts';
import type { PackUnpackItem } from './packUnpack.consts';
import type { NodeId } from 'web';

export { getIteratorInfo } from './utils/nodeUtils';

export function useCreateIteratorFromNode() {
  const flowView = useFlowView();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const { track } = useAnalytics();

  const createIterator = useCallback(
    (nodeId: NodeId, items: PackUnpackItem[]) => {
      const info = getIteratorInfo(items);
      if (!info) return;

      const node = flowView.getNodeNullable(nodeId);
      if (!node) return;

      const parentNode = node.parentId ? flowView.getNodeNullable(node.parentId) : undefined;
      const absolutePosition = parentNode ? getAbsolutePositionFromRelative(node.position, parentNode) : node.position;

      const nodeWidth = node.width ?? DEFAULT_NODE_WIDTH;
      const position = {
        x: absolutePosition.x + nodeWidth + PACK_UNPACK_GAP,
        y: absolutePosition.y,
      };

      const newNode = createNewNode(info.iteratorId, nodeTypes, position, info.data);
      if (newNode) {
        flowView.addNewNode(newNode);
        track(
          PACK_UNPACK_EVENT_NAMES.CREATE_ITERATOR_FROM_NODE,
          { nodeType: node.type, itemCount: items.length },
          TrackTypeEnum.Product,
        );
      }
    },
    [flowView, nodeTypes, track],
  );

  return { createIterator };
}
